import { useCallback, useEffect, useState } from 'react';
import { connectLinkedin, disconnectLinkedin, getLinkedinStatus } from '../services/linkedin';

// disconnected = pas de token ; connected = token valide ; expired = token expiré (reconnecter) ;
// unavailable = zone non couverte (hors UE/EEE/Suisse) ; unknown = statut illisible (on ne SAIT pas
// s'il existe un token) ; error = échec technique de connexion.
export type LinkedinState =
  | 'loading'
  | 'disconnected'
  | 'connected'
  | 'expired'
  | 'unavailable'
  | 'unknown'
  | 'error';

export function useLinkedinConnection() {
  const [state, setState] = useState<LinkedinState>('loading');
  const [busy, setBusy] = useState(false);
  // Échec de déconnexion : remonté à l'écran, jamais avalé (retrait de consentement RGPD).
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getLinkedinStatus();
      setState(s.connected ? 'connected' : s.expired ? 'expired' : 'disconnected');
    } catch {
      // Statut illisible ≠ déconnecté : un token peut exister côté serveur. On ne bloque pas
      // l'audit par captures, mais on laisse la déconnexion accessible (§8 de la politique).
      setState('unknown');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await connectLinkedin();
      if (outcome === 'connected') await refresh();
      else if (outcome === 'unavailable') setState('unavailable');
      else if (outcome === 'error') setState('error');
      // 'cancelled' → on ne change rien
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await disconnectLinkedin();
      setState('disconnected');
    } catch {
      // L'échec doit être VISIBLE : sans message, l'utilisateur croit son consentement retiré.
      setError('Déconnexion impossible. Réessaie.');
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, error, connect, disconnect, refresh };
}
